import { Link } from "react-router-dom";
import { ExternalLink, HeartHandshake, MapPin, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { media, partner } from "../data/siteData";
import { useSEO } from "../hooks/useSEO";

const values = [
  { icon: HeartHandshake, title: "Community first", copy: "A welcoming beach culture for first-timers, travellers, families, and local riders." },
  { icon: MapPin, title: "Rooted in Kannur", copy: "Experiences shaped by the northern Kerala shoreline, weather windows, and local pace." },
  { icon: Sparkles, title: "Progress with ease", copy: "Coaching that keeps safety clear, confidence steady, and the mood relaxed." },
];

export default function About() {
  useSEO({
    title: "About — A Modern Surf Club with Kerala Warmth",
    description: "Kannur Surf Club is a community-driven surf club rooted in Kerala's northern coast — safe coaching, mellow waves, and a welcoming beach culture.",
    path: "/about",
  });

  return (
    <main data-testid="about-page" className="bg-[#0A0F1C] text-white">
      <section data-testid="about-hero-section" className="relative min-h-[78vh] px-5 pt-32 md:px-10">
        <img data-testid="about-hero-image" src={media.about} alt="Kannur Surf Club community gathering by the Kerala coast" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-center opacity-45" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,#0A0F1C_8%,rgba(10,15,28,0.78),rgba(10,15,28,0.3)),radial-gradient(circle_at_30%_30%,rgba(255,94,58,0.24),transparent_32%)]" />
        <div className="relative mx-auto max-w-7xl pt-16">
          <p data-testid="about-hero-kicker" className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF5E3A]">About Us</p>
          <h1 data-testid="about-hero-title" className="mt-6 max-w-4xl font-heading text-5xl font-black leading-none tracking-tighter sm:text-6xl">A modern surf club with Kerala warmth.</h1>
          <p data-testid="about-hero-description" className="mt-7 max-w-2xl text-base leading-8 text-slate-300 md:text-lg">
            Kannur Surf Club is built for people who want more than a checklist activity — it’s a place to learn, stay, move, meet people, and fall into the rhythm of the sea.
          </p>
        </div>
      </section>

      <section data-testid="about-story-section" className="px-5 py-24 md:px-10 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p data-testid="about-story-kicker" className="text-xs font-bold uppercase tracking-[0.28em] text-[#FFC059]">The story</p>
            <h2 data-testid="about-story-title" className="mt-5 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl">A social collective shaped by the surfers of Kannur.</h2>
          </div>
          <div data-testid="about-story-copy" className="space-y-6 text-base leading-8 text-slate-400">
            <p>
              Kannur Surf Club is a social collective brought together by the surfers of Kannur — a group of locals who kept showing up for the ocean and slowly built a club around it.
            </p>
            <p>
              Activity operations are run by our partner, {partner.name}, led by Rajesh, who looks after surf and kite progression, equipment, and the day-to-day water programme.
            </p>
            <p>
              The stays are curated by other locals from the collective, so wherever you land you’re looking after your trip alongside people who actually live this coastline.
            </p>
            <p>
              Beyond surf, the club hosts wave sessions, music nights, and other coastal experiences that bring travellers and locals into the same room — beach culture as it’s actually lived in Kannur.
            </p>
          </div>
        </div>

        <div data-testid="about-values-grid" className="mx-auto mt-16 grid max-w-7xl gap-8 md:grid-cols-3">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div data-testid={`about-value-card-${index}`} key={value.title} className="border border-white/10 bg-[#131B2F] p-8 transition-all duration-300 hover:-translate-y-2 hover:border-[#FF5E3A]/60">
                <Icon className="h-9 w-9 text-[#FF5E3A]" />
                <h3 data-testid={`about-value-title-${index}`} className="mt-6 font-heading text-2xl font-bold text-white">{value.title}</h3>
                <p data-testid={`about-value-copy-${index}`} className="mt-4 text-sm leading-7 text-slate-400">{value.copy}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section data-testid="about-partner-section" className="px-5 pb-12 md:px-10">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,rgba(13,148,136,0.18),rgba(255,94,58,0.14))] p-8 backdrop-blur-xl md:p-12">
          <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <p data-testid="about-partner-kicker" className="text-xs font-bold uppercase tracking-[0.28em] text-[#FFC059]">In partnership with</p>
              <h2 data-testid="about-partner-title" className="mt-4 font-heading text-3xl font-bold text-white sm:text-4xl">{partner.name}</h2>
              <p data-testid="about-partner-copy" className="mt-4 max-w-2xl text-base leading-8 text-slate-300">
                {partner.name}, led by Rajesh, runs the activity side of the club — surf and kite progression, equipment, instructors, and the day-to-day water programme. They’re the operators on the beach for everything you book through Kannur Surf Club.
              </p>
            </div>
            <Button data-testid="about-partner-link" asChild variant="outline" className="h-12 rounded-xl border-white/20 bg-white/5 px-7 font-semibold uppercase tracking-[0.16em] text-white backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-white/35 hover:bg-white/10 hover:text-white">
              <a href={partner.url} target="_blank" rel="noreferrer">
                Visit {partner.name} <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section data-testid="about-cta-section" className="px-5 pb-24 md:px-10 md:pb-32">
        <div className="mx-auto max-w-7xl border border-white/10 bg-[#131B2F] p-8 md:p-12">
          <h2 data-testid="about-cta-title" className="font-heading text-3xl font-bold text-white sm:text-4xl">Come for the sunrise. Stay for the people.</h2>
          <p data-testid="about-cta-copy" className="mt-4 max-w-2xl text-base leading-8 text-slate-400">Ask us what’s best for your season, skill level, and trip length.</p>
          <Button data-testid="about-cta-contact-button" asChild className="mt-8 h-12 rounded-none bg-[#FF5E3A] px-8 font-semibold uppercase tracking-[0.16em] text-white hover:bg-[#FF7B5C]">
            <Link to="/contact">Start a Conversation</Link>
          </Button>
        </div>
      </section>
    </main>
  );
}